import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Plus, Trash2, Pencil, Loader2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { adminListScience, adminCreateScience, adminUpdateScience, adminDeleteScience } from "@/lib/api";
import { getScienceIcon, SCIENCE_ICON_OPTIONS, SCIENCE_GROUP_OPTIONS } from "@/data/science";
import { ImageUploader } from "@/components/admin/ImageUploader";

const emptyArticle = { name: "", slug: "", group: "Technology", icon: "atom", tagline: "", intro: "", image: "", sections: [], usedIn: [], sources: [] };

export const ScienceManager = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyArticle);
  const [usedInText, setUsedInText] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    adminListScience().then(setArticles).catch(() => setArticles([])).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditing(null);
    setForm(emptyArticle);
    setUsedInText("");
    setOpen(true);
  };
  const openEdit = (a) => {
    setEditing(a.id);
    setForm({ ...emptyArticle, ...a });
    setUsedInText((a.usedIn || []).join("\n"));
    setOpen(true);
  };

  const set = (k) => (ev) => setForm((s) => ({ ...s, [k]: ev.target.value }));

  const addSection = () => setForm((s) => ({ ...s, sections: [...(s.sections || []), { heading: "", body: "" }] }));
  const updateSection = (i, key, val) => setForm((s) => {
    const arr = [...(s.sections || [])];
    arr[i] = { ...arr[i], [key]: val };
    return { ...s, sections: arr };
  });
  const removeSection = (i) => setForm((s) => ({ ...s, sections: (s.sections || []).filter((_, idx) => idx !== i) }));

  const addSource = () => setForm((s) => ({ ...s, sources: [...(s.sources || []), { label: "", url: "" }] }));
  const updateSource = (i, key, val) => setForm((s) => {
    const arr = [...(s.sources || [])];
    arr[i] = { ...arr[i], [key]: val };
    return { ...s, sources: arr };
  });
  const removeSource = (i) => setForm((s) => ({ ...s, sources: (s.sources || []).filter((_, idx) => idx !== i) }));

  const save = async () => {
    if (!form.name || form.name.trim().length < 1) {
      toast.error("Kailangan ng pangalan ng element");
      return;
    }
    const payload = {
      ...form,
      usedIn: usedInText.split("\n").map((x) => x.trim()).filter(Boolean),
      sections: (form.sections || []).map((s) => ({ heading: (s.heading || "").trim(), body: (s.body || "").trim() })).filter((s) => s.heading || s.body),
      sources: (form.sources || []).map((s) => ({ label: (s.label || "").trim(), url: (s.url || "").trim() })).filter((s) => s.url),
    };
    setSaving(true);
    try {
      if (editing) await adminUpdateScience(editing, payload);
      else await adminCreateScience(payload);
      toast.success(editing ? "Na-update ang element" : "Naidagdag ang element");
      setOpen(false);
      load();
    } catch (err) {
      toast.error("Hindi na-save ang element");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (a) => {
    try {
      await adminDeleteScience(a.id);
      setArticles((prev) => prev.filter((x) => x.id !== a.id));
      toast.success("Na-delete ang element");
      load();
    } catch (err) {
      toast.error("Hindi na-delete");
    }
  };

  return (
    <div>
      <div className="flex flex-col gap-3 rounded-xl border border-border bg-card p-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-lg font-semibold">Science articles ({articles.length})</h2>
          <p className="text-sm text-muted-foreground">I-edit ang details at sources ng bawat infused element (Graphene, Terahertz, atbp.).</p>
        </div>
        <Button onClick={openNew} data-testid="science-add-button"><Plus className="mr-2 h-4 w-4" /> Bagong element</Button>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {loading && <div className="p-6 text-sm text-muted-foreground">Naglo-load...</div>}
        {!loading && articles.map((a) => {
          const Icon = getScienceIcon(a.icon || a.name);
          return (
            <Card key={a.id} className="rounded-xl border-border bg-card p-4" data-testid="admin-science-card">
              <div className="flex items-start gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/12 text-primary">
                  <Icon className="h-5 w-5" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold">{a.name}</p>
                  <p className="mt-0.5 text-xs font-medium text-foreground">{a.group} &middot; {(a.sources || []).length} sources</p>
                </div>
              </div>
              <div className="mt-3 flex gap-2">
                <Button variant="outline" size="sm" className="h-8" onClick={() => openEdit(a)} data-testid="admin-science-edit"><Pencil className="mr-1 h-3.5 w-3.5" /> Edit</Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="ghost" size="sm" className="h-8 text-muted-foreground hover:text-destructive" data-testid="admin-science-delete"><Trash2 className="h-3.5 w-3.5" /></Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent className="dark">
                    <AlertDialogHeader>
                      <AlertDialogTitle>I-delete ang element?</AlertDialogTitle>
                      <AlertDialogDescription>Sigurado ka bang idi-delete ang “{a.name}”? Hindi na ito maibabalik.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Kanselahin</AlertDialogCancel>
                      <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={() => remove(a)}>I-delete</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </Card>
          );
        })}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="dark max-h-[90vh] max-w-lg overflow-y-auto" data-testid="science-form-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "I-edit ang element" : "Bagong element"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs font-medium text-foreground">Pangalan</Label>
              <Input className="mt-1 h-10" value={form.name} onChange={set("name")} placeholder="hal. Graphene" data-testid="science-form-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-medium text-foreground">Grupo</Label>
                <Select value={form.group} onValueChange={(v) => setForm((s) => ({ ...s, group: v }))}>
                  <SelectTrigger className="mt-1 h-10" data-testid="science-form-group"><SelectValue /></SelectTrigger>
                  <SelectContent className="dark">
                    {SCIENCE_GROUP_OPTIONS.map((g) => <SelectItem key={g.value} value={g.value}>{g.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-medium text-foreground">Icon</Label>
                <Select value={form.icon} onValueChange={(v) => setForm((s) => ({ ...s, icon: v }))}>
                  <SelectTrigger className="mt-1 h-10" data-testid="science-form-icon"><SelectValue /></SelectTrigger>
                  <SelectContent className="dark">
                    {SCIENCE_ICON_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs font-medium text-foreground">Slug (URL) — opsyonal</Label>
              <Input className="mt-1 h-10" value={form.slug} onChange={set("slug")} placeholder="hal. graphene (auto kung blangko)" data-testid="science-form-slug" />
            </div>
            <div>
              <Label className="text-xs font-medium text-foreground">Tagline</Label>
              <Input className="mt-1 h-10" value={form.tagline} onChange={set("tagline")} />
            </div>
            <div>
              <Label className="text-xs font-medium text-foreground">Intro</Label>
              <Textarea className="mt-1 min-h-24" value={form.intro} onChange={set("intro")} />
            </div>

            <div>
              <Label className="text-xs font-medium text-foreground">Hero image (optional)</Label>
              <p className="mt-0.5 text-[11px] leading-relaxed text-muted-foreground">Ito ang malaking larawan sa itaas ng element page at ang thumbnail sa cards. Mag-upload o mag-paste ng URL — makikita mo agad ang preview sa ibaba.</p>
              <div className="mt-1.5">
                <ImageUploader wide value={form.image} onChange={(v) => setForm((s) => ({ ...s, image: v }))} testid="science-form-image" />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between">
                <Label className="text-xs font-medium text-foreground">Mga section (heading + body)</Label>
                <Button type="button" variant="outline" size="sm" className="h-7" onClick={addSection} data-testid="science-form-section-add"><Plus className="mr-1 h-3.5 w-3.5" /> Dagdag</Button>
              </div>
              <div className="mt-2 space-y-3">
                {(form.sections || []).map((s, i) => (
                  <div key={i} className="rounded-lg border border-border bg-background/60 p-2" data-testid={`science-form-section-${i}`}>
                    <div className="flex items-center gap-2">
                      <Input className="h-9 flex-1" value={s.heading || ""} onChange={(e) => updateSection(i, "heading", e.target.value)} placeholder="Heading" data-testid={`science-form-section-heading-${i}`} />
                      <Button type="button" variant="ghost" size="icon" className="h-9 w-9 shrink-0 text-muted-foreground hover:text-destructive" onClick={() => removeSection(i)} data-testid={`science-form-section-remove-${i}`}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                    <Textarea className="mt-2 min-h-16" value={s.body || ""} onChange={(e) => updateSection(i, "body", e.target.value)} placeholder="Body / paliwanag" data-testid={`science-form-section-body-${i}`} />
                  </div>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-xs font-medium text-foreground">Ginagamit sa (isa bawat linya)</Label>
              <Textarea className="mt-1 min-h-16" value={usedInText} onChange={(e) => setUsedInText(e.target.value)} placeholder={"Product 1\nProduct 2"} data-testid="science-form-usedin" />
            </div>

            <div>
              <div className="flex items-center justify-between">
                <Label className="text-xs font-medium text-foreground">Mga Sanggunian / Sources (label + URL)</Label>
                <Button type="button" variant="outline" size="sm" className="h-7" onClick={addSource} data-testid="science-form-source-add"><Plus className="mr-1 h-3.5 w-3.5" /> Dagdag</Button>
              </div>
              <div className="mt-2 space-y-2">
                {(form.sources || []).length === 0 && (
                  <p className="text-xs font-medium text-foreground">Wala pang source. I-tap ang “Dagdag” para maglagay ng link.</p>
                )}
                {(form.sources || []).map((s, i) => (
                  <div key={i} className="rounded-lg border border-border bg-background/60 p-2" data-testid={`science-form-source-${i}`}>
                    <div className="flex items-center gap-2">
                      <Input className="h-9 flex-1" value={s.label || ""} onChange={(e) => updateSource(i, "label", e.target.value)} placeholder="Label (hal. Wikipedia)" data-testid={`science-form-source-label-${i}`} />
                      <Button type="button" variant="ghost" size="icon" className="h-9 w-9 shrink-0 text-muted-foreground hover:text-destructive" onClick={() => removeSource(i)} data-testid={`science-form-source-remove-${i}`}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                    <div className="mt-2 flex items-center gap-2">
                      <ExternalLink className="h-4 w-4 shrink-0 text-muted-foreground" />
                      <Input className="h-9 flex-1" value={s.url || ""} onChange={(e) => updateSource(i, "url", e.target.value)} placeholder="https://..." data-testid={`science-form-source-url-${i}`} />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setOpen(false)}>Kanselahin</Button>
              <Button onClick={save} disabled={saving} data-testid="science-form-save">
                {saving ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sina-save...</> : "I-save"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
